import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain } from '@feathr/blackbox';
import { DebouncedInput, Input, TanStackTable, Toolbar } from '@feathr/components';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import domainsColumns from './domainsColumns';

import * as styles from './DomainsTable.css';

interface IFilters {
  content_domain?: { $regex?: string };
  email_domain?: { $regex?: string };
}

interface IQuery {
  __raw__: {
    $or: IFilters[];
  };
  // TODO: Filter by Content Serving and Email Sending status
}

/** The legacy `<Table>`'s default page size; one of the sizes the pagination dropdown offers. */
const DEFAULT_PAGE_SIZE = 20;
const INITIAL_SORT: TCollectionTableSort = [{ desc: true, id: 'date_created' }];

function getNameFilters(name = ''): IQuery {
  return {
    __raw__: {
      $or: [{ email_domain: { $regex: name } }, { content_domain: { $regex: name } }],
    },
  };
}

function DomainsTable(): JSX.Element {
  const { Domains } = useStore();
  const { t } = useTranslation();
  const [filters, setFilters] = useState<IQuery>(getNameFilters());

  function handleChangeNameQuery(newValue?: string): void {
    setFilters(getNameFilters(newValue));
  }

  const tableProps = useCollectionTable<Domain>(Domains, {
    defaultParams: { filters },
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  // TanStack re-measures column widths when the array identity changes, so build it once per `t`.
  const columns = useMemo(() => domainsColumns(t), [t]);

  return (
    <>
      <Toolbar align={'left'} className={styles.filtersToolbar}>
        <DebouncedInput<string> onChange={handleChangeNameQuery}>
          {(liveValue, onChangeLiveValue): JSX.Element => (
            <Input
              isClearable={true}
              onChange={onChangeLiveValue}
              placeholder={t('Search by name...')}
              prefix={getIconForAction('search')}
              type={'text'}
              value={liveValue}
            />
          )}
        </DebouncedInput>
      </Toolbar>
      <TanStackTable<Domain>
        {...tableProps}
        columns={columns}
        noDataText={t('You have not registered any domains yet.')}
      />
    </>
  );
}

export default observer(DomainsTable);
